/**
 * Parse a server timestamp that denotes an absolute instant in time.
 *
 * The backend serializes naive UTC datetimes without a timezone suffix
 * (e.g. "2026-05-09T08:59:00"). A suffix-less ISO datetime is interpreted by
 * `new Date()` as browser-local time, which shifts the displayed instant by the
 * viewer's UTC offset. We treat a suffix-less value as UTC by appending `Z`.
 *
 * Scope: ONLY for fields that represent an absolute instant (created_at,
 * updated_at, finished_at, last_synced_at, ...). Do NOT use this for
 * account-timezone "data dates" (report/metric day buckets) — those carry
 * account-tz semantics and must keep their own handling. Also not for
 * date-only values ("2026-05-09"): appending `Z` yields an invalid date.
 */
export function parseServerDate(value: string): Date {
  const hasTimezone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value)
  return new Date(hasTimezone ? value : `${value}Z`)
}

/**
 * Local 24-hour "HH:MM" stamp for a server instant — the chat transcript's
 * time-of-day format, shared by message rows and review cards so both read
 * the same clock. Null for a missing or unparsable value.
 */
export function formatServerTimeOfDay(
  value: string | null | undefined,
): string | null {
  if (!value) return null
  const d = parseServerDate(value)
  if (Number.isNaN(d.getTime())) return null
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`
}
